import { useContext, useEffect, useState } from "react"
import ConfigurationContext from "../context/ConfigurationContext.js"
import Environment from "../domain/Environment.js"
import type { Branding } from "../schemata/biometry/Branding.js"
import type { UUIDv4 } from "../schemata/shared/UUIDv4.js"
import FetchBrandingAsync from "../support/branding/FetchBrandingAsync.js"
import HTTPClient from "../support/http/HTTPClient.js"

/**
 * @description
 *	How long the landing waits for the branding before painting its middle
 *	zone without it. It is far below the timeout of the other requests of this
 *	application, because nothing of what the human being has to do depends on
 *	the branding.
 */
const BRANDING_TIMEOUT_IN_MILLISECONDS = 3 * 1000

/**
 * @description
 *	Gets the branding of an integration. The "settled" flag tells whether the
 *	backend already answered, failed or timed out, so the caller can hold the
 *	pieces whose very presence depends on the branding.
 *
 * @remarks
 *	The identifier must be the validated one, never the raw query parameter.
 *	Without an identifier there is nothing to wait for, so the state settles
 *	empty.
 *
 *	However the request ends, the state settles: the landing cannot hold
 *	its explanation forever.
 *
 *	The effect guards itself with an "ignore" flag instead of aborting the
 *	request, because the transport has no abort signal and React strict mode
 *	runs every effect twice.
 *
 *	The rejection branch is what an unusable origin lands on: the fetch
 *	resolves to undefined for everything that the backend or the network can
 *	do, and rejects only when the configured origin cannot be turned into a
 *	URL. It settles the same way, because the landing has to keep working
 *	even while that configuration is wrong.
 */
export default function useBranding(
	integrationId: UUIDv4 | undefined
): Readonly<{ branding: Branding | undefined; settled: boolean }> {
	const configuration = useContext(ConfigurationContext)
	const [state, setState] = useState<{ branding: Branding | undefined; settled: boolean }>({
		branding: undefined,
		settled: false
	})
	useEffect(() => {
		if (integrationId === undefined) {
			setState({ branding: undefined, settled: true })
			return
		}
		let ignore = false
		const settle = (branding: Branding | undefined): void => {
			if (!ignore) {
				setState({ branding, settled: true })
			}
		}
		FetchBrandingAsync({
			httpClient: new HTTPClient({
				insecureTLS: configuration.ENVIRONMENT === Environment.Local,
				keepAlive: false,
				proxy: _ => _
			}),
			integrationId,
			origin: configuration.BIOMETRY_ORIGIN,
			timeoutInMilliseconds: BRANDING_TIMEOUT_IN_MILLISECONDS
		}).then(settle, () => settle(undefined))
		return () => {
			ignore = true
		}
	}, [configuration.BIOMETRY_ORIGIN, configuration.ENVIRONMENT, integrationId])
	return state
}
